import { Box, List } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import { CustomerOrganizationsResult } from "../../types/queries";
import ListItemComponent from "../ListItemComponent";
import { ListHeader } from "../styles";
import { useAppSelector } from "../../../../app/hooks";
import { selectAllCustomerContracts } from "../../customerContractsSlice";
import AddButton from "../../../../common/components/functional/addButton/AddButton";
import { canExpertAddCustomerOrganization } from "../../../user/accessRightChecks";
import { CreateOrganizationDialog } from "../../create/organization/CreateOrganizationDialog";
import { useState } from "react";
import { TabValue } from "../../types/types";

type Props = {
  filters: CustomerOrganizationsResult[];
  selectedTab: TabValue;
};

const CustomerOrganizationFilter = ({ filters, selectedTab }: Props) => {
  const { t } = useTranslation("customers");
  const [searchParams, setSearchParams] = useSearchParams();
  const { customerCompanyId } = useParams();

  const selectedCustomerOrgId = searchParams.get("org");
  const selectedEezyCompanyId = searchParams.get("ecom");

  const customerContracts = useAppSelector((state) =>
    selectAllCustomerContracts(state),
  );

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const selectEcomSearchParam = () => {
    // contracts are fetched with currently selected customerOrgId
    const currentOrgHasContractWithSelectedEcom = customerContracts.find(
      (contract) => contract.eezyCompany.id === selectedEezyCompanyId,
    );

    const firstEezyCompanyFromFilterList =
      customerContracts?.length > 0 &&
      customerCompanyId === customerContracts[0].customerCompanyId &&
      customerContracts[0].eezyCompany.id;

    if (currentOrgHasContractWithSelectedEcom) {
      return selectedEezyCompanyId;
    } else {
      return firstEezyCompanyFromFilterList || null;
    }
  };

  const handleFilterChange = (org: CustomerOrganizationsResult) => {
    // check if current organization has contract with selectedEezyCompanyId
    const selectedEcomParam = selectEcomSearchParam();

    setSearchParams({
      org: org.id,
      ...(selectedEcomParam && { selectedEcomParam }),
    });
  };

  const bottomBorderBy = (orgId: string) => {
    const isSelected = selectedCustomerOrgId === orgId;
    const isFirst = orgId === filters[0].id;
    return isSelected || isFirst ? "none" : "1px solid #000A441C";
  };

  return (
    <>
      <Box flex={1} maxWidth="295px">
        <List
          sx={{ marginTop: "22px", width: "100%", maxWidth: "311px" }}
          subheader={<ListHeader variant="header">{t("companies")}</ListHeader>}
        >
          {filters.map((org, index) => (
            <ListItemComponent
              key={org.id}
              primary={org.name}
              secondary={org.businessId}
              selected={org.id === selectedCustomerOrgId}
              onClick={() => handleFilterChange(org)}
              sx={{
                marginTop: index === 0 ? "0.5rem" : "unset",
                marginLeft: index === 0 ? "unset" : "24px",
                borderBottom: bottomBorderBy(org.id),
              }}
            />
          ))}
          {selectedTab === "basicInfo" && (
            <AddButton
              sx={{ marginTop: "1rem", marginLeft: "32px" }}
              label={t("newSuborganization")}
              onClick={() => setIsCreateDialogOpen(true)}
              variant="contained"
              accessRightChecks={[canExpertAddCustomerOrganization]}
            />
          )}
        </List>
      </Box>
      <CreateOrganizationDialog
        customerCompanyId={customerCompanyId as string}
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />
    </>
  );
};

export default CustomerOrganizationFilter;
